import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiStar } from 'react-icons/fi';
import axios from 'axios';
import { trackPageView, trackFormSubmission } from '../utils/analytics';
import './Pricing.css';

// Bring Your Own Cloud plans
const byocPlans = [
        {
          id: 'basic',
          name: 'Basic',
          price: '$25',
          period: '/node/host/month',
          description: 'Includes one cluster and up to 40 hosts/nodes.',
          features: [
            'Time-based retention for logs, metrics, events, and traces.',
            'Includes email support for all inquiries.',
            'Billed annually at $999/month',
            'Monthly plans start at $1199/month'
          ],
          popular: false,
          cta: 'CONTACT US'
        },
        {
          id: 'standard',
          name: 'Standard',
          price: '$25',
          period: '/node/host/month',
          description: 'Includes one cluster and up to 80 hosts/nodes.',
          features: [
            'Time-based retention for logs, metrics, events, and traces.',
            'Includes email plus dedicated Slack support for real-time assistance.',
            'Billed annually at $1999/month',
            'Monthly plans start at $2399/month'
          ],
          popular: true,
          cta: 'CONTACT US'
        },
        {
          id: 'premium',
          name: 'Premium',
          price: '$20',
          period: '/node/host/month',
          description: 'Multiple clusters and up to 250 hosts /nodes.',
          features: [
            'Time-based retention for logs, metrics, events, and traces.',
            'Includes email, dedicated Slack and migration assistance.',
            'Billed annually at $4999/month',
            'Monthly plans start at $5999/month'
          ],
          popular: false,
          cta: 'CONTACT US'
        },
        {
          id: 'enterprise',
          name: 'Enterprise',
          price: 'Custom',
          period: 'starts at $9999/month',
          description: 'Unlimited clusters and hosts/nodes.',
          features: [
            'Advanced retention policies for logs, metrics, events, and traces.',
            'Customized feature packaging and integrations.',
            'SSO, RBAC, flexible feature packaging and integrations.',
            'Includes email, dedicated 24/7 Slack & On-call, migration and configuration assistance.',
            'Free unlimited non-prod usage.',
            'Price capping on multi-year licenses.'
        ],
        popular: false,
        cta: 'CONTACT US'
      }
    ];

// LogCrest Cloud plans (different pricing structure)
const LogCrestCloudPlans = [
        {
          id: 'basic-cloud',
          name: 'Basic',
          price: '$0.25',
          period: '/GB',
          description: 'Ingested',
          features: [
            'Includes 2 TB of logs, metrics, events, and traces per month.',
            '15-day retention for logs, events, and traces.',
            '1-month retention for metrics.',
            'Includes email support for all inquiries.',
            'Billed annually at $399/month',
            'Monthly plans start at $499/month'
          ],
          popular: false,
          cta: 'Start Free Trial'
        },
        {
          id: 'standard-cloud',
          name: 'Standard',
          price: '$0.20',
          period: '/GB',
          description: 'Ingested',
          features: [
            'Includes 5 TB of logs, metrics, events, and traces per month.',
            '15-day retention for logs, events, and traces.',
            '1-month retention for metrics.',
            'Includes email plus dedicated Slack support for real-time assistance.',
            'Billed annually at $999/month',
            'Monthly plans start at $1,199/month'
          ],
          popular: true,
          cta: 'Start Free Trial'
        },
        {
          id: 'premium-cloud',
          name: 'Premium',
          price: '$0.20',
          period: '/GB',
          description: 'Ingested',
          features: [
            'Includes 15 TB of logs, metrics, events, and traces per month.',
            '15-day retention for logs, events, and traces.',
            '3-month retention for metrics.',
            'Includes email, dedicated Slack and migration assistance.',
            'Billed annually at $2,999/month',
            'Monthly plans start at $3,599/month'
          ],
          popular: false,
          cta: 'Start Free Trial'
        },
        {
          id: 'enterprise-cloud',
          name: 'Enterprise',
          price: 'Custom',
          period: 'starts at $9999/month',
          description: '',
          features: [
            'Custom retention policies for logs, events, and traces.',
            '15-month retention for metrics.',
            'SSO, RBAC, flexible feature packaging and integrations.',
            'Includes email, dedicated 24/7 Slack & On-call, migration and configuration assistance.',
            'Free unlimited non-prod usage.',
            'Price capping on multi-year licenses.'
          ],
          popular: false,
          cta: 'Start Free Trial'
        }
      ];

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeTab, setActiveTab] = useState('byoc'); // 'byoc' or 'LogCrest-cloud'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    plan: 'standard',
    team_size: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  // Use ref to prevent duplicate API calls
  const hasFetchedPlans = useRef(false);

  useEffect(() => {
    // Prevent duplicate API calls
    if (hasFetchedPlans.current) {
      return;
    }
    
    hasFetchedPlans.current = true;
    
    const fetchPlans = () => {
      console.log('Using default pricing plans...');
      
      // Set initial plans based on active tab
      const initialPlans = activeTab === 'byoc' ? byocPlans : LogCrestCloudPlans;
      setPlans(initialPlans);
      
      // Set Standard plan as selected by default
      const standardPlan = initialPlans.find(plan => plan.popular);
      if (standardPlan) {
        setSelectedPlan(standardPlan);
      }
      setLoading(false);
    };

    fetchPlans();

    // Track page visit with comprehensive analytics
    trackPageView('/pricing', {
      page_title: 'LogCrest Pricing - Choose Your Plan',
      page_category: 'pricing'
    });
  }, [activeTab]);

  // Handle tab changes
  useEffect(() => {
    // Get the current plans based on active tab
    const currentPlans = activeTab === 'byoc' ? byocPlans : LogCrestCloudPlans;
    setPlans(currentPlans);
    
    // Reset selected plan when switching tabs
    const popularPlan = currentPlans.find(plan => plan.popular);
    if (popularPlan) {
      setSelectedPlan(popularPlan);
      setFormData(prev => ({
        ...prev,
        plan: popularPlan.id
      }));
    }
  }, [activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePlanSelect = (plan) => {
    // If clicking the same plan, deselect it
    if (selectedPlan?.id === plan.id) {
      setSelectedPlan(null);
      setFormData(prev => ({
        ...prev,
        plan: ''
      }));
    } else {
      // Select the new plan (this automatically deselects others)
      setSelectedPlan(plan);
      setFormData(prev => ({
        ...prev,
        plan: plan.id
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Track form submission
      trackFormSubmission('pricing_request', {
        form_fields: Object.keys(formData),
        plan: formData.plan,
        team_size: formData.team_size
      });

      await axios.post('/api/pricing/request', formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        plan: '',
        team_size: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to submit pricing request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: 'Can I change my plan at any time?',
      answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any billing differences.'
    },
    {
      question: 'Is there a free trial available?',
      answer: 'Yes, we offer a 14-day free trial for all plans. No credit card required to get started.'
    },
    {
      question: 'What happens if I exceed my node limit?',
      answer: 'We\'ll notify you when you\'re approaching your limit. You can upgrade your plan or purchase additional nodes as needed.'
    },
    {
      question: 'Do you offer custom enterprise solutions?',
      answer: 'Yes, our Enterprise plan includes custom integrations, on-premise deployment, and dedicated support. Contact us to discuss your specific needs.'
    },
    {
      question: 'What support options are available?',
      answer: 'Starter includes email support, Professional includes priority support, and Enterprise includes 24/7 dedicated support with SLA guarantees.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.'
    }
  ];

  if (loading) {
    return (
      <div className="pricing-loading">
        <div className="container">
          <div className="spinner"></div>
          <p>Loading pricing plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing">
      {/* Pricing Tabs */}
      <section className="pricing-tabs">
        <div className="container">
          <motion.div
            className="pricing-tabs-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="pricing-tabs-wrapper">
              <button
                className={`pricing-tab ${activeTab === 'byoc' ? 'active' : ''}`}
                onClick={() => handleTabChange('byoc')}
              >
                Bring Your Own Cloud
              </button>
              <button
                className={`pricing-tab ${activeTab === 'LogCrest-cloud' ? 'active' : ''}`}
                onClick={() => handleTabChange('LogCrest-cloud')}
              >
                LogCrest Cloud
              </button>
            </div>
            <p className="pricing-tabs-description enterprise-support">EVERY PLAN COMES WITH GUARANTEED ENTERPRISE-GRADE SUPPORT.</p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className={`pricing-section ${selectedPlan && !selectedPlan.popular ? 'non-popular-selected' : ''}`}>
        <div className="container">
        <div className="pricing-grid">
          {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                className={`pricing-card ${plan.popular ? 'popular' : ''} ${selectedPlan?.id === plan.id ? 'selected' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                onClick={() => handlePlanSelect(plan)}
                style={{ cursor: 'pointer' }}
              >
                {plan.popular && (
                  <div className="popular-badge">
                    <FiStar />
                    Most Popular
                  </div>
                )}
                
                <div className="pricing-header">
                  <h3>{plan.name}</h3>
                  <div className={`pricing-price ${plan.id === 'enterprise' || plan.id === 'enterprise-cloud' ? 'enterprise-pricing' : ''}`}>
                    {plan.id === 'enterprise' || plan.id === 'enterprise-cloud' ? (
                      <>
                        <div className="enterprise-price">{plan.price}</div>
                        <div className="enterprise-period">{plan.period}</div>
                      </>
                    ) : (
                      <>
                        <span className="price">{plan.price}</span>
                        <span className="period">{plan.period}</span>
                      </>
                    )}
                  </div>
                  <p className={`pricing-description ${plan.description === 'Ingested' ? 'ingested' : ''}`}>{plan.description}</p>
                </div>

                <ul className="pricing-features">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <FiCheck />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="pricing-cta">
                  <Link to="/demo" className="btn btn-primary">
                    {plan.cta}
                    <FiArrowRight />
                  </Link>
                </div>
              </motion.div>
          ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="pricing-contact">
        <div className="container">
          <motion.div
            className="pricing-contact-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="contact-info">
              <h2>Need help choosing a plan?</h2>
              <p>
                Our team is here to help you find the perfect plan for your infrastructure needs. 
                Get a personalized recommendation and pricing quote.
              </p>
              <div className="contact-benefits">
                <div className="benefit-item">
                  <FiCheck />
                  <span>Personalized recommendations</span>
                </div>
                <div className="benefit-item">
                  <FiCheck />
                  <span>Custom pricing for large teams</span>
                </div>
                <div className="benefit-item">
                  <FiCheck />
                  <span>Free migration assistance</span>
                </div>
              </div>
            </div>

            <form className="pricing-form" onSubmit={handleSubmit}>
              <h3>Get a custom quote</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="team_size" className="form-label">Team Size</label>
                  <select
                    id="team_size"
                    name="team_size"
                    value={formData.team_size}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Select team size</option>
                    <option value="1-5">1-5 people</option>
                    <option value="6-20">6-20 people</option>
                    <option value="21-50">21-50 people</option>
                    <option value="51-100">51-100 people</option>
                    <option value="100+">100+ people</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="plan" className="form-label">Interested Plan</label>
                <select
                  id="plan"
                  name="plan"
                  value={formData.plan}
                  onChange={handleInputChange}
                  className="form-input"
                >
                  <option value="">Select a plan</option>
                  {plans.map(plan => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.price} {plan.period}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-input form-textarea"
                  placeholder="Tell us about your infrastructure and monitoring needs..."
                />
              </div>

              {submitStatus === 'success' && (
                <div className="form-success">
                  <FiCheck />
                  Thank you! We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="form-error">
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    Get Custom Quote
                    <FiArrowRight />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="faq-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h4>{faq.question}</h4>
                  <p>{faq.answer}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="pricing-comparison" style={{ padding: '6rem 0', background: 'var(--background-alt)' }}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem', color: 'var(--text-primary)' }}>
              Compare LogCrest with Alternatives
            </h2>
            <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)', marginBottom: '3rem', maxWidth: '600px', margin: '0 auto 3rem' }}>
              See how LogCrest compares to other observability platforms
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
              <Link 
                to="/logcrest-vs-datadog" 
                style={{ 
                  padding: '2rem', 
                  background: 'var(--background)', 
                  borderRadius: '16px', 
                  border: '1px solid var(--border-color)', 
                  textDecoration: 'none', 
                  color: 'var(--text-primary)',
                  transition: 'all 0.3s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'var(--primary-600)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 131, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--primary-600)' }}>
                  LogCrest vs Datadog
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                  Compare features, pricing, and capabilities
                </p>
              </Link>
              <Link 
                to="/logcrest-vs-splunk" 
                style={{ 
                  padding: '2rem', 
                  background: 'var(--background)', 
                  borderRadius: '16px', 
                  border: '1px solid var(--border-color)', 
                  textDecoration: 'none', 
                  color: 'var(--text-primary)',
                  transition: 'all 0.3s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'var(--primary-600)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 131, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--primary-600)' }}>
                  LogCrest vs Splunk
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                  Modern alternative to Splunk
                </p>
              </Link>
              <Link 
                to="/logcrest-vs-newrelic" 
                style={{ 
                  padding: '2rem', 
                  background: 'var(--background)', 
                  borderRadius: '16px', 
                  border: '1px solid var(--border-color)', 
                  textDecoration: 'none', 
                  color: 'var(--text-primary)',
                  transition: 'all 0.3s',
                  display: 'block'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.borderColor = 'var(--primary-600)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(255, 131, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--primary-600)' }}>
                  LogCrest vs New Relic
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                  APM and observability comparison
                </p>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
